import { apiClient } from './api-client'
import {
  LoginRequest,
  RegisterRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  AuthResponse,
  PartnerUser
} from '@/types/auth'

export const authApi = {
  register: async (data: RegisterRequest): Promise<{ message: string; userId: string }> => {
    const response = await apiClient.post('/auth/register', data)
    return response.data
  },

  login: async (data: LoginRequest): Promise<AuthResponse> => {
    const response = await apiClient.post('/auth/login', data)
    const { token, refreshToken, user } = response.data

    // Store tokens
    localStorage.setItem('auth_token', token)
    if (refreshToken) {
      localStorage.setItem('refresh_token', refreshToken)
    }

    // Cache user data (including partnerId)
    localStorage.setItem('partner_user', JSON.stringify(user))

    return response.data
  },

  logout: async (): Promise<void> => {
    try {
      await apiClient.post('/auth/logout')
    } finally {
      // Clear tokens and cached user data even if request fails
      localStorage.removeItem('auth_token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('partner_user')
    }
  },

  verifyEmail: async (data: { email: string; code: string }): Promise<{ message: string }> => {
    const response = await apiClient.post('/auth/verify-email', data)
    return response.data
  },

  resendOtp: async (data: { email: string }): Promise<{ message: string }> => {
    const response = await apiClient.post('/auth/resend-otp', data)
    return response.data
  },

  forgotPassword: async (data: ForgotPasswordRequest): Promise<{ message: string }> => {
    const response = await apiClient.post('/auth/forgot-password', data)
    return response.data
  },

  resetPassword: async (data: ResetPasswordRequest): Promise<{ message: string }> => {
    const response = await apiClient.post('/auth/reset-password', data)
    return response.data
  },

  refreshToken: async (refreshToken: string): Promise<{ token: string; refreshToken: string }> => {
    const response = await apiClient.post('/auth/refresh', { refreshToken })
    const { token, refreshToken: newRefreshToken } = response.data

    // Update tokens
    localStorage.setItem('auth_token', token)
    localStorage.setItem('refresh_token', newRefreshToken)

    return response.data
  },

  getCurrentUser: async (): Promise<PartnerUser> => {
    const response = await apiClient.get('/auth/me')

    // Cache user data
    localStorage.setItem('partner_user', JSON.stringify(response.data))

    return response.data
  }
}

export const getStoredToken = () => localStorage.getItem('auth_token')
export const getStoredRefreshToken = () => localStorage.getItem('refresh_token')
export const isAuthenticated = () => Boolean(getStoredToken())

export const getCachedUser = (): PartnerUser | null => {
  const userStr = localStorage.getItem('partner_user')
  if (!userStr) return null
  try {
    return JSON.parse(userStr)
  } catch {
    return null
  }
}

export const getPartnerId = (): string | null => {
  const user = getCachedUser()
  return user?.partnerId || null
}
